/**
 * ClientCatalogSwitcher — 3PL client-catalog routing (/products).
 *
 * Scope switcher mounted in the global TopHeader left slot (same anchoring as
 * the /inventory client filter, `HeaderSlotContext`): it changes WHOSE catalog
 * the page is reading, so it belongs to the app chrome, not to the page's
 * filter cluster.
 *
 * Deliberately NOT `ClientFilterDropdown`:
 *  - That component is built around the `ALL_CLIENTS` sentinel (an "All
 *    clients (n)" row plus a clear-to-all button) and an org list derived from
 *    the rows currently in view. There is no "all clients" catalog to read —
 *    `listProductsGrouped` takes exactly one `targetOrganizationId` — so the
 *    sentinel would be an option that cannot be honoured.
 *  - Trigger + option styling are matched to it deliberately (h-8 trigger,
 *    brand-purple `Link2` provenance mark per DESIGN.md §13.4/§13.9) so the
 *    two dropdowns read as one family.
 *
 * Renders NOTHING when there are no active clients — single-tenant chrome is
 * byte-identical to today.
 *
 * KNOWN THRESHOLD: DESIGN.md §13.9 switches these client pickers to a cmdk
 * Combobox at 7+ options. No 3PL org is near that today; if one gets there,
 * lift `ClientFilterCombobox` out of `ClientFilterDropdown` into a shared
 * primitive and use it here rather than growing a third copy.
 */
import { useTranslation } from 'react-i18next';
import { Link2 } from 'lucide-react';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ConnectedClient } from '../hooks/useConnectedClients';

/** Radix Select rejects an empty-string value, so own-catalog needs a token. */
const OWN_CATALOG = '__own__';

export interface ClientCatalogSwitcherProps {
  /** null = the caller's own catalog. */
  value: string | null;
  /** Emits null for own catalog, else the client org id. */
  onChange: (clientOrgId: string | null) => void;
  /** Active connected clients at the floor(s) in view, deduped, sorted. */
  clients: ConnectedClient[];
}

export function ClientCatalogSwitcher({ value, onChange, clients }: ClientCatalogSwitcherProps) {
  const { t } = useTranslation('inventory');

  if (clients.length === 0) return null;

  const selectedClient = value ? clients.find((c) => c.id === value) : undefined;

  return (
    <Select
      value={value ?? OWN_CATALOG}
      onValueChange={(next) => onChange(next === OWN_CATALOG ? null : next)}
    >
      <SelectTrigger
        className="h-8 w-[220px] gap-2 text-xs"
        aria-label={t('clientCatalog.switcher.ariaLabel')}
      >
        {selectedClient ? (
          <span className="flex min-w-0 items-center gap-2">
            <Link2 className="h-3.5 w-3.5 shrink-0 text-threadheads-purple" aria-hidden="true" />
            <span className="truncate">{selectedClient.name}</span>
          </span>
        ) : (
          <SelectValue placeholder={t('clientCatalog.switcher.myCatalog')} />
        )}
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={OWN_CATALOG}>{t('clientCatalog.switcher.myCatalog')}</SelectItem>
        <SelectSeparator />
        <SelectGroup>
          <SelectLabel className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t('clientCatalog.switcher.clientsGroup')}
          </SelectLabel>
          {clients.map((client) => (
            <SelectItem key={client.id} value={client.id}>
              <span className="flex items-center gap-2">
                <Link2
                  className="h-3.5 w-3.5 shrink-0 text-threadheads-purple"
                  aria-hidden="true"
                />
                <span className="truncate">{client.name}</span>
              </span>
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
